import { passwordScore } from '@sd/contracts';
import type { TitlebarInfo } from '@sd/contracts/ipc';
import { Fragment, useCallback, useId, type CSSProperties, type ReactNode } from 'react';
import { Icon } from '../../icons/Icon';
import { Button, Notice } from '../../ui/controls';
import { useToast } from '../../ui/overlays';

/** `.brand` with the sd-messenger logo. `bn` hides the name on very narrow screens. */
export function Brand() {
  return (
    <div className="brand">
      <span className="logo">
        <Icon name="logo" />
      </span>
      <span className="bn">sd-messenger</span>
    </div>
  );
}

/** `.web-top.glass` pill header used by the web pages. */
export function WebTop({ children, right }: { children?: ReactNode; right?: ReactNode }) {
  return (
    <header className="web-top glass">
      {children ?? <Brand />}
      {right ? <div className="wt-right">{right}</div> : null}
    </header>
  );
}

/** `.urlbar` showing the verified company address the page is served from. The address has its own box: an ellipsis needs one. */
export function UrlBar({ url }: { url: string }) {
  return (
    <span className="urlbar">
      <Icon name="lock" size="xs" />
      <span className="ell">{url}</span>
    </span>
  );
}

/**
 * Desktop title bar for a frameless window. Never draws fake window controls:
 * native traffic lights / controls overlay occupy the insets (see TitlebarInfo).
 */
export function WindowTitlebar({ titlebar, title }: { titlebar: TitlebarInfo | null; title: string }) {
  if (!titlebar || (titlebar.kind !== 'mac-inset' && titlebar.kind !== 'overlay')) return null;
  // Symmetric padding keeps the title centred like the design while leaving the native controls free.
  const inset = Math.max(16, titlebar.leftInset, titlebar.rightInset);
  const style = { WebkitAppRegion: 'drag', paddingLeft: inset, paddingRight: inset } as CSSProperties;
  return (
    <header className="titlebar" style={style}>
      <div className="tb-title">{title}</div>
    </header>
  );
}

/** `.stepper` progress (design renderConnect). `current` is 1-based; values past the end mark all done. */
export function Stepper({ steps, current }: { steps: readonly string[]; current: number }) {
  return (
    <div className="stepper" aria-label={`단계 ${Math.min(current, steps.length)} / ${steps.length}`}>
      {steps.map((name, index) => {
        const n = index + 1;
        const state = current === n ? ' on' : current > n ? ' done' : '';
        return (
          <Fragment key={name}>
            {index ? <span className="ln" /> : null}
            <span className={`sp${state}`} aria-current={current === n ? 'step' : undefined}>
              <i>{current > n ? '✓' : n}</i>
              <span>{name}</span>
            </span>
          </Fragment>
        );
      })}
    </div>
  );
}

const STRENGTH_HINTS = ['8자 이상 입력해 주세요', '조금 더 길게 만들어 주세요', '좋아요', '안전해요', '아주 안전해요'];

/** Password field with the design's `.pw-meter` (same scoring as the server). */
export function PasswordField({
  value,
  onChange,
  label = '비밀번호',
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  const inputId = useId();
  const score = passwordScore(value);
  return (
    <div>
      <label className="label" htmlFor={inputId}>
        {label}
      </label>
      <div className="field">
        <input
          id={inputId}
          type="password"
          placeholder="8자 이상, 숫자 포함"
          value={value}
          autoComplete="new-password"
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
      <div className={`pw-meter s${score}`} aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
      <p className="hint" aria-live="polite">
        {STRENGTH_HINTS[score]}
      </p>
    </div>
  );
}

/** `<a>` styled as a design `.btn` (deep links, signed downloads). `external` opens another site in a new tab. */
export function LinkButton({
  href,
  className,
  icon,
  download,
  external = false,
  onClick,
  children,
}: {
  href: string;
  className: string;
  icon?: Parameters<typeof Icon>[0]['name'];
  download?: string;
  external?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <a
      className={className}
      href={href}
      download={download}
      onClick={onClick}
      style={{ textDecoration: 'none' }}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {icon ? <Icon name={icon} size="sm" /> : null}
      {children}
    </a>
  );
}

/** Clipboard helper with the design's "복사했어요" toast. */
export function useCopy() {
  const toast = useToast();
  return useCallback(
    async (text: string, message = '복사했어요') => {
      try {
        await navigator.clipboard.writeText(text);
        toast(message);
      } catch {
        toast('복사하지 못했어요. 직접 선택해서 복사해 주세요');
      }
    },
    [toast],
  );
}

/** `.copy-row` monospace pill with a copy button. */
export function CopyRow({ text, display, label = '복사' }: { text: string; display?: string; label?: string }) {
  const copy = useCopy();
  return (
    <div className="copy-row">
      <span className="ell">{display ?? text}</span>
      <Button size="sm" icon="copy" onClick={() => void copy(text)}>
        {label}
      </Button>
    </div>
  );
}

/** Centered progress text used while a screen loads. */
export function LoadingState({ text }: { text: string }) {
  return (
    <div className="iv-state" role="status">
      <span className="big" style={{ background: 'var(--blue-soft)', color: 'var(--blue-ink)' }}>
        <Icon name="sync" size="lg" className="spin" />
      </span>
      <p style={{ margin: 0, color: 'var(--ink-2)', fontSize: 14 }}>{text}</p>
    </div>
  );
}

/** An admin panel whose data could not be loaded: the reason and another try. */
export function LoadErrorNotice({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Notice tone="danger" icon="alertc">
      {message}{' '}
      <button type="button" className="linkbtn" onClick={onRetry}>
        다시 시도
      </button>
    </Notice>
  );
}

/** `.iv-state` result block (big icon, title, body) from the invite design. */
export function StateBlock({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'ok' | 'warn' | 'danger' | 'blue';
  icon: Parameters<typeof Icon>[0]['name'];
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="iv-state">
      <span className="big" style={{ background: `var(--${tone}-soft)`, color: `var(--${tone === 'blue' ? 'blue-ink' : tone})` }}>
        <Icon name={icon} size="lg" />
      </span>
      <h2>{title}</h2>
      {children ? <p style={{ margin: 0, color: 'var(--ink-2)', fontSize: 14, lineHeight: 1.6 }}>{children}</p> : null}
    </div>
  );
}

/** A web-browser label for "로그인된 기기 관리" (e.g. "웹 브라우저 · Chrome"). */
export function webDeviceLabel(userAgent: string = globalThis.navigator?.userAgent ?? ''): string {
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Whale\//.test(userAgent)
      ? 'Whale'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Chrome\//.test(userAgent)
          ? 'Chrome'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : '브라우저';
  return `웹 브라우저 · ${browser}`;
}

/** `sd-messenger://connect?server=…` deep link: carries only the address, never credentials. */
export function connectDeepLink(serverUrl: string): string {
  return `sd-messenger://connect?server=${encodeURIComponent(serverUrl)}`;
}
